










































import { Component, Vue } from 'vue-property-decorator';
import gql from 'graphql-tag';
import CenterList from '@/components/CenterList.vue';
import LaneSelect from '@/components/LaneSelect.vue';
import Modal from '@/components/Modal.vue';
import LinkSession from '@/components/LinkSession.vue';
import StartSession from '@/components/StartSession.vue';
import EndSession from '@/components/EndSession.vue';
import store from '@/store';

type SelectMode = 'watch' | 'play' | 'manage' | 'default';

type LaneConfig = 'CROSS_LANE' | 'SINGLE_LANE' | 'MULTI_LANE';

interface ISession {
  id: string;
  startTime: string;
  endTime: string;
  laneConfig: LaneConfig;
  activeLanes: number[];
  lastThrowTime: string | null;
  lastPhoneLinkTime: string | null;
  isLeague: boolean;
}

// A lane is lit while its session has bowled within this long: a session exists from the
// league's start packet, up to an hour before the first ball, until the next group replaces it.
const IN_PLAY_MINUTES = 45;

// A phone's activation lights its lane until the first ball, for this long at most.
const PHONE_LINK_MINUTES = 60;

// How often the open page reads the center again and looks at the clock.
const REFRESH_SECONDS = 15;

// A league session this long past its last ball is over: the lane is free to a phone, and the server
// ends the session when one comes (Footbowl:Sessions:LeftoverLeagueMinutes).
const LEFTOVER_LEAGUE_MINUTES = 60;

interface ICenter {
  id: string;
  centerCode: string;
  name: string;
  lanes: number;
  activeSessions: ISession[];
}

const UserQuery = gql`
  {
    user {
      id
      bowlingNickname
      activeSessions {
        bowlingPosition
        center {
          id
        }
        lanes
      }
    }
  }
`;

const CenterQuery = gql`
  query Center($id: ID) {
    center(id: $id) {
      id
      centerCode
      name
      country
      city
      stateOrProvince
      postalCode
      phone
      lanes
      activeSessions(all: true) {
        id
        startTime
        endTime
        activeLanes
        laneConfig
        lastThrowTime
        lastPhoneLinkTime
        isLeague
      }
    }
  }
`;

const ApolloOptions = Vue.extend({
  apollo: {
    user: {
      query: UserQuery,
      skip() {
        return !store.getters['auth/authenticated'];
      },
    },
    center: {
      query: CenterQuery,
      fetchPolicy: 'cache-and-network',
      pollInterval: REFRESH_SECONDS * 1000,
      variables() {
        return { id: this.centerId };
      },
      skip() {
        return !this.centerId;
      },
    },
  },
});

@Component<SelectGame>({
  components: {
    CenterList,
    LaneSelect,
    Modal,
    LinkSession,
    StartSession,
    EndSession,
  },
  watch: {
    '$route.query.centerId'(centerId?: string) {
      (this as SelectGame).centerId = centerId || null;
    },
  },
})
export default class SelectGame extends ApolloOptions {
  public center?: ICenter;
  public lane?: number;

  public showEndSession = false;
  public showStartSession = false;
  public showJoinGame = false;

  public user: any = null;
  public centerId: string | null = null;

  // The clock the lit lanes read, so a lane goes dark on time when the data has not changed.
  public now = Date.now();
  private clock?: number;

  get mode(): SelectMode {
    const m = this.$route.query.mode;

    if (!m || ['watch', 'play', 'manage'].indexOf(m) < 0) {
      return 'default';
    }

    return m as SelectMode;
  }

  get adminMode() {
    return this.mode === 'manage';
  }

  public created() {
    switch (this.mode) {
      case 'watch':
        break;
      case 'manage':
      case 'play': {
        if (!store.getters['auth/authenticated']) {
          return this.unauthorized();
        }
        break;
      }
      default:
        this.$router.back();
        break;
    }
  }

  public mounted() {
    this.centerId = this.$route.query.centerId || null;
    this.clock = window.setInterval(() => {
      this.now = Date.now();
    }, REFRESH_SECONDS * 1000);
  }

  public beforeDestroy() {
    window.clearInterval(this.clock);
  }

  public unauthorized() {
    this.$router.back();
  }

  public onCenterSelected(centerId: string) {
    this.centerId = centerId;
    this.$apollo.queries.center.skip = false;

    this.$router.push(
      `/selectGame?mode=${this.mode}&centerId=${this.centerId}`,
    );
  }

  public onLaneSelected(lane: number) {
    const session = this.getSession(lane);
    this.lane = lane;

    if (this.mode === 'manage') {
      return this.manageSessions(lane, session);
    }

    // Watch mode only works for games in play
    if (this.mode === 'watch' && session && this.isInPlay(session)) {
      this.watchGame();
    }

    if (this.mode === 'play') {
      this.playGame();
    }
    // if there is one session, go to it
    // if (session) {
    //   if (this.mode === 'watch') {
    //     return this.watchGame();
    //   } else {
    //     return this.playGame();
    //   }
    // } else {
    //   this.manageSessions(lane, undefined);
    // }
  }

  public onSessionStarted() {
    this.showStartSession = false;
    this.lane = undefined;

    this.$apollo.queries.center.refetch();
  }

  public onSessionEnded() {
    this.showEndSession = false;
    this.lane = undefined;

    this.$apollo.queries.center.refetch();
  }

  public onSessionLinked() {
    if (!this.center || !this.lane) {
      return;
    }

    this.$router.push(`/footbowl/${this.center.centerCode}/lane/${this.lane}`);
  }

  public getLanePair(lane: number) {
    return [lane, lane % 2 === 1 ? lane + 1 : lane - 1];
  }

  public watchGame() {
    if (!this.center || !this.lane) {
      return;
    }

    this.$router.push(`/footbowl/${this.center.centerCode}/lane/${this.lane}`);
  }

  public playGame() {
    if (!this.center || !this.lane) {
      return;
    }

    // Check if this user is already a part of this game
    if (this.user && this.user.activeSessions) {
      const exists = this.user.activeSessions.some(
        (s: any) =>
          s.center.id === (this.center as any).id &&
          s.lanes.indexOf(this.lane) > -1,
      );
      if (exists) {
        return this.onSessionLinked();
      }
    }

    this.showJoinGame = true;
  }

  public manageSessions(lane: number, session?: ISession) {
    if (session) {
      this.showEndSession = true;
    } else {
      this.showStartSession = true;
    }
  }

  public getSession(lane: number) {
    return this.activeSessions.find((s) => s.activeLanes.indexOf(lane) > -1);
  }

  public isLaneActive(lane: number) {
    return this.activeLanes.indexOf(lane) > -1;
  }

  public getSessionDictionary(lane: number) {
    return this.getLanePair(lane)
      .map((l) => this.getSession(l))
      .reduce((obj, s) => (s ? { ...obj, [s.id]: s.activeLanes } : obj), {} as {
        [id: string]: number[];
      });
  }

  public isPairedActive(lane: number) {
    const paired = lane % 2 === 0 ? lane - 1 : lane + 1;
    return this.isFree(paired);
  }

  // No session on the lane, or a league's long over: a phone starts its own there.
  public isFree(lane: number) {
    const session = this.getSession(lane);
    return !session || (session.isLeague && !!session.lastThrowTime
      && !this.isWithin(session.lastThrowTime, LEFTOVER_LEAGUE_MINUTES));
  }

  get activeLanes() {
    return this.activeSessions.reduce(
      (prev, s) => [...prev, ...s.activeLanes],
      [] as number[],
    );
  }

  // Lit lanes, on every page and for an admin too: sessions that have bowled recently, or a phone
  // has just activated. Joining, starting and ending a session keep using every active session,
  // since a session on a dark lane still exists.
  public isInPlay(session: ISession) {
    return this.isWithin(session.lastThrowTime, IN_PLAY_MINUTES)
      || this.isWithin(session.lastPhoneLinkTime, PHONE_LINK_MINUTES)
      || (!session.isLeague && !session.lastThrowTime && this.isWithin(session.startTime, PHONE_LINK_MINUTES));
  }

  private isWithin(time: string | null, minutes: number) {
    if (!time) {
      return false;
    }
    const last = Date.parse(time);
    return !isNaN(last) && this.now - last < minutes * 60 * 1000;
  }

  get sessionsInPlay() {
    return this.activeSessions.filter((s) => this.isInPlay(s));
  }

  get lanesInPlay() {
    return this.sessionsInPlay.reduce(
      (prev, s) => [...prev, ...s.activeLanes],
      [] as number[],
    );
  }

  get activeSessions() {
    if (!this.center) {
      return [];
    }

    return this.center.activeSessions;
  }
}
